<script setup lang="ts">
/*
 * Token-styled text input. Variant defaults match the prototype's input chrome:
 * 1px app-border, 12px radius, 14px text, focus ring matches the prototype's
 * `.ring-focus`. Picks up id / describedby / invalid from a parent FormField.
 *
 * When `clearable` is true and the input has a value, an × button appears
 * inside the right edge. Clicking it emits `'clear'` so the parent can reset
 * any dependent state (e.g. debounced refs) that a plain v-model update cannot
 * reach synchronously.
 */
import { computed, inject, type Ref, type ComputedRef } from 'vue';

type Type = 'text' | 'email' | 'password' | 'number' | 'tel' | 'search' | 'url' | 'time';

const props = withDefaults(
  defineProps<{
    modelValue?: string | number;
    type?: Type;
    placeholder?: string;
    disabled?: boolean;
    readonly?: boolean;
    autocomplete?: string;
    name?: string;
    id?: string;
    invalid?: boolean;
    size?: 'sm' | 'md';
    clearable?: boolean;
  }>(),
  { type: 'text', size: 'md', clearable: false },
);

defineEmits<{ 'update:modelValue': [v: string | number]; clear: [] }>();

const field = inject<{
  inputId: Ref<string>;
  describedBy: ComputedRef<string>;
  invalid: ComputedRef<boolean>;
}>('formField', undefined as never);

const finalId = computed(() => props.id ?? field?.inputId.value);
const finalDescribedBy = computed(() => field?.describedBy.value || undefined);
const isInvalid = computed(() => props.invalid || field?.invalid.value || false);
const showClear = computed(() => props.clearable && !!props.modelValue);

// Single class list shared by both clearable and non-clearable branches so
// future additions (e.g. a new focus-ring token) only need one edit.
const inputClass = computed(() => [
  'w-full border bg-app-surface text-app-fg placeholder:text-app-muted transition-shadow',
  'focus:outline-none focus:ring-3 focus:ring-primary/20 focus:border-primary',
  'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
  '[&::-webkit-search-cancel-button]:hidden',
  // sm fields get a tighter radius + smaller text/placeholder to suit the
  // reduced height; md keeps the prototype's 12px radius / 14px text.
  props.size === 'sm' ? 'rounded-md py-1.5 text-sm' : 'rounded-xl py-2.5 text-base',
  // Reserve pr-7 whenever clearable — not just when the × is visible — so the
  // field width doesn't shift by 16 px when the user starts typing (NN3).
  props.clearable ? 'pl-3 pr-7' : props.size === 'sm' ? 'px-3' : 'px-3.5',
  isInvalid.value ? 'border-red-300 focus:border-red-500 focus:ring-red-100' : 'border-app-border',
]);
</script>

<template>
  <!-- Wrap in relative container only when clearable so non-clearable callers
       don't get an extra DOM node. -->
  <div v-if="clearable" class="relative">
    <input
      :id="finalId"
      :type="type"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :autocomplete="autocomplete"
      :name="name"
      :aria-invalid="isInvalid || undefined"
      :aria-describedby="finalDescribedBy"
      :class="inputClass"
      @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
    />
    <!-- tabindex="-1": intentional mouse-only convenience button.
         Keyboard users clear via Ctrl/Cmd+A → Delete or Esc (if wired by parent).
         @mousedown.prevent keeps focus in the input when the button is clicked. -->
    <button
      v-if="showClear"
      type="button"
      aria-label="Clear"
      class="absolute right-2 top-1/2 -translate-y-1/2 flex h-4 w-4 items-center justify-center rounded-full text-app-muted hover:bg-slate-100 hover:text-app-fg"
      tabindex="-1"
      @mousedown.prevent
      @click="
        $emit('update:modelValue', '');
        $emit('clear');
      "
    >
      <span aria-hidden="true" class="text-[13px] leading-none">×</span>
    </button>
  </div>

  <!-- Non-clearable: bare input, no wrapper -->
  <input
    v-else
    :id="finalId"
    :type="type"
    :value="modelValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :autocomplete="autocomplete"
    :name="name"
    :aria-invalid="isInvalid || undefined"
    :aria-describedby="finalDescribedBy"
    :class="inputClass"
    @input="$emit('update:modelValue', ($event.target as HTMLInputElement).value)"
  />
</template>
